import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { MultiColumnCard } from "components/Card/MultiColumnCard";
import { KaboodleToaster } from "components/common";
import KaboodleModal from "components/Modals/KaboodleModal";
import RedbookSearchModal from "components/RedbookSearchModal/RedbookSearchModal";
import { Tooltip } from "components/Tooltip/Tooltip";
import { useCallback, useMemo, useState } from "react";
import {
  useClonePrescriptionAllocationMutation,
  useCreatePrescriptionAllocationMutation,
  useDeletePrescriptionAllocationByIdMutation,
  useGetPrescriptionAllocationQuery,
  useMovePrescriptionAllocationMutation,
} from "services/msa-api";
import { useToken } from "utils/context";

import { getServiceField } from "../../future-medical-utils";
import { PrescriptionAllocationRow } from "../components/AllocationRow/PrescriptionAllocationRow";
import { DroppableZone } from "../components/DroppableZone/DroppableZone";
import { ALLOCATION_DND_TYPES } from "../utils/allocation-constants";
import { calculateDropPosition } from "../utils/allocation-drop-utils";

import type {
  AllocationService,
  PrescriptionAllocationRow as PrescriptionAllocationRowType,
  PrescriptionAllocationRowProps,
} from "../utils/allocation-types";
import type { RedbookDrugProduct } from "types/DataTypes";
import type { AllocationFilterValue } from "../components/AllocationFilter/AllocationFilter";

type Props = {
  isLoadingAllRows: boolean;
  service: AllocationService;
  allocationFilter?: AllocationFilterValue;
  reviewType: "MEDICAL" | "LEGAL";
};

/**
 * card for prescription allocations
 * displays included and excluded prescription allocations
 * integrates with Redbook drug database
 * handles drug search, row creation, and drag-drop between included/excluded sections
 */
export const PrescriptionAllocationCard = ({
  isLoadingAllRows,
  service,
  allocationFilter = "ALL",
  reviewType,
}: Props) => {
  const [rowToDelete, setRowToDelete] =
    useState<PrescriptionAllocationRowProps | null>(null);
  const [showRedbookSearch, setShowRedbookSearch] = useState(false);
  const [redbookTargetIsExcluded, setRedbookTargetIsExcluded] = useState(false);
  const { accessToken } = useToken();
  const [createPrescriptionAllocation, { isLoading: isCreating }] =
    useCreatePrescriptionAllocationMutation();
  const [clonePrescriptionAllocation, { isLoading: isCloning }] =
    useClonePrescriptionAllocationMutation();
  const [deletePrescriptionAllocation, { isLoading: isDeleting }] =
    useDeletePrescriptionAllocationByIdMutation();
  const [movePrescriptionAllocation, { isLoading: isMoving }] =
    useMovePrescriptionAllocationMutation();

  const { data } = useGetPrescriptionAllocationQuery(
    {
      filters: getServiceField(service, true, reviewType),
    },
    { skip: !accessToken || !service.id },
  );

  const allRows = data?.results || [];

  const isLoading =
    isCreating || isDeleting || isLoadingAllRows || isMoving || isCloning;

  // filter rows based on medicare status
  const filteredRows = useMemo(() => {
    if (allocationFilter === "ALL") {
      return allRows;
    }
    if (allocationFilter === "MEDICARE") {
      return allRows.filter(
        (row) => !(row as { non_medicare?: boolean }).non_medicare,
      );
    }
    if (allocationFilter === "NON_MEDICARE") {
      return allRows.filter(
        (row) => (row as { non_medicare?: boolean }).non_medicare,
      );
    }
    return allRows;
  }, [allRows, allocationFilter]);

  const includedRowsBase = filteredRows
    .filter((row: PrescriptionAllocationRowType) => !row.is_excluded)
    .sort(
      (a: PrescriptionAllocationRowType, b: PrescriptionAllocationRowType) =>
        a.position - b.position,
    );

  const excludedRowsBase = filteredRows
    .filter((row: PrescriptionAllocationRowType) => row.is_excluded)
    .sort(
      (a: PrescriptionAllocationRowType, b: PrescriptionAllocationRowType) =>
        a.position - b.position,
    );

  // add section metadata to rows for drop indicator logic
  const includedRows = includedRowsBase.map((row) => ({
    ...row,
    totalRowsInSection: includedRowsBase.length,
  }));

  const excludedRows = excludedRowsBase.map((row) => ({
    ...row,
    totalRowsInSection: excludedRowsBase.length,
  }));

  const handleNewRow = useCallback(
    async (is_excluded: boolean) => {
      if (accessToken) {
        try {
          await createPrescriptionAllocation({
            data: {
              name: "-",
              ndc: "-",
              dosage: 0,
              units_per_month: 0,
              units_per_year: 12,
              unit_price: 0,
              number_of_years: 0,
              ...getServiceField(service, false, reviewType),
              is_excluded,
              is_included_exposure_or_mitigation: true,
            },
          }).unwrap();
        } catch (error) {
          KaboodleToaster.error("Failed to create new row");
        }
      }
    },
    [service, createPrescriptionAllocation, reviewType],
  );

  const handleDeleteRow = useCallback(
    async (row: PrescriptionAllocationRowProps) => {
      if (accessToken) {
        try {
          await deletePrescriptionAllocation({
            id: row.id,
            data: {
              ...getServiceField(service, false, reviewType),
            },
          }).unwrap();
          setRowToDelete(null);
        } catch (error) {
          KaboodleToaster.error(`Failed to delete row: ${error}`);
          console.error("Failed to delete row:", error);
        }
      }
    },
    [deletePrescriptionAllocation, service, reviewType],
  );

  const handleCloneRow = useCallback(
    async (row: PrescriptionAllocationRowProps) => {
      if (accessToken) {
        try {
          await clonePrescriptionAllocation({
            id: row.id,
          }).unwrap();
          KaboodleToaster.success("Row cloned successfully");
        } catch (error) {
          KaboodleToaster.error("Failed to clone row");
          console.error("Failed to clone row:", error);
        }
      }
    },
    [clonePrescriptionAllocation],
  );

  const handleRowDrop = useCallback(
    async (
      draggedRow: PrescriptionAllocationRowProps,
      dropTargetRow: PrescriptionAllocationRowProps & {
        totalRowsInSection: number;
      },
    ) => {
      // don't do anything if dropping on itself
      if (draggedRow.id === dropTargetRow.id) return;

      const { targetPosition } = calculateDropPosition({
        draggedRow,
        dropTargetRow,
      });

      try {
        await movePrescriptionAllocation({
          id: draggedRow.id,
          data: {
            to_position: targetPosition,
            is_excluded: dropTargetRow.is_excluded,
          },
        }).unwrap();
      } catch (error) {
        KaboodleToaster.error("Failed to move prescription");
      }
    },
    [movePrescriptionAllocation],
  );

  const handleZoneDrop = useCallback(
    async (row: PrescriptionAllocationRowProps, newIsExcluded: boolean) => {
      if (row.is_excluded === newIsExcluded) return;

      // calculate target position - append to end of target section
      const targetRows = allRows.filter((r) => r.is_excluded === newIsExcluded);
      const toPosition = targetRows.length;

      try {
        await movePrescriptionAllocation({
          id: row.id,
          data: {
            to_position: toPosition,
            is_excluded: newIsExcluded,
          },
        }).unwrap();
      } catch (error) {
        KaboodleToaster.error("Failed to move prescription");
      }
    },
    [allRows, movePrescriptionAllocation],
  );

  const handleRedbookSearch = useCallback(
    async (drug: RedbookDrugProduct) => {
      try {
        await createPrescriptionAllocation({
          data: {
            redbook_drug_product: drug.id,
            name: drug.product_name,
            ndc: drug.ndc,
            active_ingredient: drug.active_ingredient,
            formulation: drug.formulation,
            is_generic: drug.is_generic,
            unit_price: drug.awp_unit_price,
            dosage: drug.dosage,
            ...getServiceField(service, false, reviewType),
            is_excluded: redbookTargetIsExcluded,
            is_included_exposure_or_mitigation: true,
          },
        }).unwrap();
        KaboodleToaster.success("Drug added successfully");
        setShowRedbookSearch(false);
        setRedbookTargetIsExcluded(false);
      } catch (error) {
        KaboodleToaster.error("Failed to add drug");
      }
    },
    [
      createPrescriptionAllocation,
      redbookTargetIsExcluded,
      service,
      reviewType,
    ],
  );

  const RowComponent = useCallback(
    (
      props: Omit<PrescriptionAllocationRowProps, "service"> & {
        totalRowsInSection: number;
      },
    ) => (
      <PrescriptionAllocationRow
        {...props}
        service={service}
        redbook_drug_product={props.redbook_drug_product ?? null}
        onSetRowToDelete={setRowToDelete}
        onDrop={handleRowDrop}
        onCloneRow={handleCloneRow}
      />
    ),
    [handleRowDrop, handleCloneRow],
  );

  const deleteModal = useMemo(() => {
    return (
      <>
        {rowToDelete && (
          <KaboodleModal
            isOpen={!!rowToDelete}
            onClose={() => setRowToDelete(null)}
            footer={
              <div className="flex jc-c">
                <Button
                  className="delete"
                  onClick={() => handleDeleteRow(rowToDelete)}
                >
                  Confirm Delete
                </Button>
                <Button onClick={() => setRowToDelete(null)}>Cancel</Button>
              </div>
            }
          >
            <Card heading="Delete Row">
              <p>Are you sure you want to delete this row?</p>
            </Card>
          </KaboodleModal>
        )}
      </>
    );
  }, [rowToDelete, handleDeleteRow]);

  return (
    <>
      {deleteModal}
      <RedbookSearchModal
        isOpen={showRedbookSearch}
        onClose={() => {
          setShowRedbookSearch(false);
          setRedbookTargetIsExcluded(false);
        }}
        onSelect={handleRedbookSearch}
      />
      <DroppableZone<PrescriptionAllocationRowProps>
        dndType={ALLOCATION_DND_TYPES.PRESCRIPTION}
        onDrop={(item) => handleZoneDrop(item, false)}
      >
        <MultiColumnCard
          classNames="allocation-prescription included"
          onNewRow={() => handleNewRow(false)}
          heading="Included Prescription Allocations"
          showRedbookSearchButton={true}
          onRedbookSearchClick={() => {
            setRedbookTargetIsExcluded(false);
            setShowRedbookSearch(true);
          }}
          columnNames={[
            "",
            <Tooltip key="exposure" content="Exposure">
              E
            </Tooltip>,
            <Tooltip key="mitigation" content="Mitigation">
              M
            </Tooltip>,
            <Tooltip key="generic" content="Generic">
              Gen
            </Tooltip>,
            "Name",
            "Active Ingredient",
            "Form",
            "NDC",
            "Dosage",
            "Units / Month",
            "Unit Price",
            "Months / Yr",
            "# of Years",
            "Total",
            "",
            "",
            "",
          ]}
          initialRows={includedRows}
          RowComponent={RowComponent}
          getRowKey={(row) => row.id}
          isLoading={isLoading}
        />
      </DroppableZone>

      <DroppableZone<PrescriptionAllocationRowProps>
        dndType={ALLOCATION_DND_TYPES.PRESCRIPTION}
        onDrop={(item) => handleZoneDrop(item, true)}
      >
        <MultiColumnCard
          classNames="allocation-prescription"
          onNewRow={() => handleNewRow(true)}
          heading="Excluded Prescription Allocations"
          showRedbookSearchButton={true}
          onRedbookSearchClick={() => {
            setRedbookTargetIsExcluded(true);
            setShowRedbookSearch(true);
          }}
          columnNames={[
            "",
            <Tooltip key="exposure" content="Exposure">
              E
            </Tooltip>,
            <Tooltip key="mitigation" content="Mitigation">
              M
            </Tooltip>,
            <Tooltip key="non-medicare" content="Non/Medicare">
              N/M
            </Tooltip>,
            <Tooltip key="generic" content="Generic">
              Gen
            </Tooltip>,
            "Name",
            "Active Ingredient",
            "Form",
            "NDC",
            "Dosage",
            "Units / Month",
            "Unit Price",
            "Months / Yr",
            "# of Years",
            "Total",
            "",
            "",
            "",
          ]}
          initialRows={excludedRows}
          RowComponent={RowComponent}
          getRowKey={(row) => row.id}
          isLoading={isLoading}
        />
      </DroppableZone>
    </>
  );
};

export default PrescriptionAllocationCard;
